import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { certificateService } from '../../services/certificateService';

const CertificateViewer = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const courseId = searchParams.get('courseId');
  
  const [certificate, setCertificate] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    if (!courseId) {
      setError('Course ID is required');
      setLoading(false);
      return;
    }

    const loadCertificate = async () => {
      try {
        const [certResponse, courseResponse] = await Promise.all([
          api.get(`/certificates/course/${courseId}`),
          api.get(`/courses/${courseId}`)
        ]);

        if (certResponse.data.success) {
          setCertificate(certResponse.data.data.certificate);
        } else {
          setError('Certificate not found');
        }

        if (courseResponse.data.success) {
          setCourse(courseResponse.data.data.course);
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load certificate');
      } finally {
        setLoading(false);
      }
    };

    loadCertificate();
  }, [courseId]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'Jun.', 'Jul.', 'Aug.', 'Sep.', 'Oct.', 'Nov.', 'Dec.'];
    return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
  };

  const handleDownload = async () => {
    if (!certificate?.id) return;
    
    setDownloading(true);
    try {
      await certificateService.downloadCertificate(certificate.id);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#E2F2FF] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading certificate...</p>
        </div>
      </div>
    );
  }

  if (error || !certificate) {
    return (
      <div className="min-h-screen bg-[#E2F2FF] flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-md p-8 max-w-md">
          <div className="text-red-600 mb-4">
            <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold mb-2 text-gray-800 text-center">Error</h2>
          <p className="text-gray-600 mb-4 text-center">{error || 'Certificate not found'}</p>
          <button
            onClick={() => navigate('/dashboard/modules')}
            className="w-full px-4 py-2 bg-primary text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const fullName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Student';

  return (
    <div className="min-h-screen bg-[#E2F2FF] py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard/modules')}
            className="px-4 py-2 bg-white text-primary rounded-lg hover:bg-gray-100 flex items-center gap-2 shadow-sm"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back
          </button>
          <div className="flex items-center gap-3">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 shadow-md disabled:opacity-50"
            >
              {downloading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF Certificate
                </>
              )}
            </button>
          </div>
        </div>

        <div
          className="mx-auto relative select-none"
          style={{ width: '100%', maxWidth: '1100px' }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <img
            src="/cert_r21.jpg"
            alt="Certificate preview"
            draggable={false}
            className="w-full h-auto block shadow-2xl pointer-events-none select-none"
          />
          {/* Transparent overlay to discourage right-click/drag saving of the preview. */}
          <div className="absolute inset-0" aria-hidden="true" />
        </div>

        {/* Info section */}
        <div className="mt-6 text-center text-gray-600 text-sm">
          <p>This certificate verifies completion of the R21's Fast Track: Science of Reading course.</p>
        </div>
      </div>
    </div>
  );
};

export default CertificateViewer;
